import React, { useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { SupplierGroupSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { Input } from "components/Shared/mui-formik-inputs";
import { selectPermissions, selectPermissionsStatus, getAllPermissions } from "redux/slices/permissionsSlice";
import PermissionsManager from "components/Permissions/PermissionsManager";

const useStyles = makeStyles(() => ({
  subheading: {
    fontWeight: 500,
    fontSize: "1.25rem",
    marginTop: 32,
    marginBottom: 16,
  },
}));

const RoleForm = ({ initialValues = {}, onSubmit, action }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const permissions = useSelector(selectPermissions);
  const loading = useSelector(selectPermissionsStatus);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllPermissions());
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: SupplierGroupSchema,
    initialValues: {
      name: "",
      permissions: [],
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let processedPermissions;

        if (action === "EDIT") {
          processedPermissions = values.permissions;
        } else {
          const positivePermissions = values.permissions.filter(id => id > 0);
          const uncheckedDefaultPermIds = values.permissions
            .filter(id => id < 0)
            .map(id => -id);
          const defaultPermIds = permissions
            .filter(perm => perm.default && !uncheckedDefaultPermIds.includes(perm.id))
            .map(perm => perm.id);
          processedPermissions = [...new Set([...positivePermissions, ...defaultPermIds])];
        }

        const payload = {
          name: values.name,
          permissions: processedPermissions,
        };

        await onSubmit(payload);
      } catch (err) {
        console.error("Submit error:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
  } = formik;

  // Convert array of selected permission IDs to a flat boolean object map
  const permissionsValueMap = useMemo(() => {
    const map = {};
    permissions.forEach(perm => {
      map[perm.id] = false;
    });

    if (action === "EDIT") {
      values.permissions.forEach(id => {
        if (id > 0) map[id] = true;
      });
    } else {
      permissions.forEach(perm => {
        const isChecked = values.permissions.includes(perm.id) ||
          (perm?.default && !values.permissions.includes(-perm.id));
        map[perm.id] = isChecked;
      });
    }
    return map;
  }, [permissions, values.permissions, action]);

  // Convert flat boolean object map back to array of permission IDs
  const handlePermissionsChange = (updatedMap) => {
    if (action === "EDIT") {
      const checkedIds = Object.entries(updatedMap)
        .filter(([_, checked]) => checked)
        .map(([id]) => Number(id));
      setFieldValue("permissions", checkedIds);
    } else {
      const updatedArray = [];
      permissions.forEach(perm => {
        const checked = updatedMap[perm.id];
        if (checked) {
          if (!perm.default) {
            updatedArray.push(perm.id);
          }
        } else {
          if (perm.default) {
            updatedArray.push(-perm.id);
          }
        }
      });
      setFieldValue("permissions", updatedArray);
    }
  };

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const closeHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.roles)
      : history.push(PATHS.admin.rolesDetail.replace(":id", id));
  };

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Role") : t("Edit Role")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>

      <Typography className={classes.subheading}>{t("Basic Data")}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Input
            name="name"
            label={t("Name")}
            value={values.name}
            onChange={handleChange}
            errors={formik.errors}
            required
            disabled={action !== "ADD" && values?.name === 'Admin'}
          />
        </Grid>
      </Grid>

      <Typography className={classes.subheading}>{t("Permissions")}</Typography>
      {!permissions.length ? (
        <CircularProgress />
      ) : (
        <PermissionsManager
          permissions={permissions}
          value={permissionsValueMap}
          onChange={handlePermissionsChange}
          disabled={false}
        />
      )}
    </Box>
  );
};

export default RoleForm;
